import { DatapointStatus, lazyLogSigsDatapoint, PerformanceDatapoint } from 'owa-analytics';
import setAttachmentOriginalUrl from 'owa-attachment-model-store/lib/actions/setAttachmentOriginalUrl';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import getAttachmentFileType from 'owa-attachment-model-store/lib/utils/getAttachmentFileType';
import { saveAttachmentsToCloud, SaveToCloudStatus } from 'owa-attachment-savetocloud';
import { ClientAttachmentId } from 'owa-client-ids';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import SaveToCloudNotificationPayload from 'owa-service/lib/contract/SaveToCloudNotificationPayload';
import { action } from 'satcheljs/lib/legacy';
import ActionMessageId from '../schema/ActionMessageId';
import ActionType from '../schema/ActionType';
import AttachmentFullViewState from '../schema/AttachmentFullViewState';
import { setSaveToCloudStatusHelper } from '../utils/setSaveToCloudStatusHelper';
import addCompletedAction from './addCompletedAction';
import { getIsViewingInProviderSupported } from './initialization/createAttachmentFullViewStrategy';
import setOngoingActionAndActionMessage from './setOngoingActionAndActionMessage';
import updateIsViewingInProviderSupported from './updateIsViewingInProviderSupported';

export default action('saveToCloud')(function saveToCloud(
    attachment: AttachmentFullViewState,
    completeHandler: (succeeded: boolean) => void,
    entryPoint: string
) {
    const datapoint: PerformanceDatapoint = new PerformanceDatapoint('AttachmentSaveToCloud');
    setSaveToCloudStatusHelper.run(SaveToCloudStatus.saving, attachment.attachmentId);
    saveToCloudPreProcess(attachment);
    const notificationHandler = (payload: SaveToCloudNotificationPayload): boolean => {
        const succeeded = payload.ResultCode === AttachmentResultCode.Success;

        saveToCloudPostProcess(attachment, succeeded, succeeded ? payload.Item.Location : null);

        completeHandler(succeeded);

        logSaveToCloudDatapoint(
            datapoint,
            attachment.attachmentId,
            payload.ResultCode,
            DatapointStatus.ServerError,
            payload.Exception,
            entryPoint
        );
        return true;
    };

    const serviceCallFailureCallback = (
        error: string,
        resultCode: AttachmentResultCode,
        datapointStatus: DatapointStatus
    ): void => {
        saveToCloudPostProcess(attachment, false, null);
        completeHandler(false);
        logSaveToCloudDatapoint(
            datapoint,
            attachment.attachmentId,
            resultCode,
            datapointStatus,
            error,
            entryPoint
        );
    };

    saveAttachmentsToCloud(
        attachment.attachmentId,
        null,
        notificationHandler,
        serviceCallFailureCallback
    );
});

function logSaveToCloudDatapoint(
    datapoint: PerformanceDatapoint,
    attachmentId: ClientAttachmentId,
    resultCode: AttachmentResultCode,
    datapointErrorStatus: DatapointStatus,
    exception: string,
    entryPoint: string
) {
    const attachmentModel = getAttachment(attachmentId);
    datapoint.addCustomData({
        entryPoint: entryPoint,
        result: resultCode,
    });

    datapoint.addCosmosOnlyData(
        JSON.stringify({
            size: attachmentModel.model.Size,
            fileType: getAttachmentFileType(attachmentModel),
        })
    );

    if (resultCode === AttachmentResultCode.Success) {
        datapoint.end();
    } else {
        datapoint.endWithError(datapointErrorStatus, new Error(exception));
    }

    lazyLogSigsDatapoint.importAndExecute('AttachmentSaved');
}

export function saveToCloudPreProcess(attachment: AttachmentFullViewState) {
    setOngoingActionAndActionMessage(
        attachment,
        ActionType.SaveToCloud,
        ActionMessageId.SaveToCloudInProgress
    );
}

export function saveToCloudPostProcess(
    attachment: AttachmentFullViewState,
    succeeded: boolean,
    location: string
) {
    if (succeeded) {
        addCompletedAction(attachment, ActionType.SaveToCloud);
        setAttachmentOriginalUrl(attachment.attachmentId, location);
        setSaveToCloudStatusHelper.run(SaveToCloudStatus.succeeded, attachment.attachmentId);
        const isViewingInProviderSupported = getIsViewingInProviderSupported(
            false /* isCloudy */,
            false /* isLink */,
            location
        );
        updateIsViewingInProviderSupported(attachment, isViewingInProviderSupported);
    } else {
        setSaveToCloudStatusHelper.run(SaveToCloudStatus.failed, attachment.attachmentId);
    }
    setOngoingActionAndActionMessage(
        attachment,
        ActionType.None,
        succeeded
            ? ActionMessageId.SaveToCloudCompletedWithIcon
            : ActionMessageId.SaveToCloudFailedWithIcon
    );
}
